import { firebase } from '../config/configureFirebase';
import { get, update, push, remove, ref, query, orderByChild, equalTo } from "firebase/database";
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';

// Action Types
export const ADMIN_QUEUE_ACTIONS = {
  FETCH_ADMIN_QUEUE: 'FETCH_ADMIN_QUEUE',
  FETCH_ADMIN_QUEUE_SUCCESS: 'FETCH_ADMIN_QUEUE_SUCCESS',
  FETCH_ADMIN_QUEUE_FAILED: 'FETCH_ADMIN_QUEUE_FAILED',
  ASSIGN_DRIVER: 'ASSIGN_DRIVER',
  ASSIGN_DRIVER_SUCCESS: 'ASSIGN_DRIVER_SUCCESS',
  ASSIGN_DRIVER_FAILED: 'ASSIGN_DRIVER_FAILED',
  CANCEL_TRIP: 'CANCEL_TRIP',
  CANCEL_TRIP_SUCCESS: 'CANCEL_TRIP_SUCCESS',
  CANCEL_TRIP_FAILED: 'CANCEL_TRIP_FAILED',
  UPDATE_ADMIN_CONFIG: 'UPDATE_ADMIN_CONFIG',
  UPDATE_ADMIN_CONFIG_SUCCESS: 'UPDATE_ADMIN_CONFIG_SUCCESS',
  UPDATE_ADMIN_CONFIG_FAILED: 'UPDATE_ADMIN_CONFIG_FAILED'
};

// Fetch all bookings in admin queue
export const fetchAdminQueue = () => async (dispatch) => {
  const { bookingRef } = firebase;
  
  dispatch({
    type: ADMIN_QUEUE_ACTIONS.FETCH_ADMIN_QUEUE,
    payload: null
  });

  try {
    // Get all bookings with ADMIN_QUEUE status
    const adminQueueQuery = query(
      bookingRef,
      orderByChild('status'),
      equalTo('ADMIN_QUEUE')
    );
    
    const snapshot = await get(adminQueueQuery);
    console.log("🔍 Admin queue snapshot exists:", snapshot.exists());
    console.log("🔍 Admin queue snapshot value:", snapshot.val());
    
    if (snapshot.exists()) {
      const adminQueueData = snapshot.val();
      const adminQueueBookings = Object.keys(adminQueueData).map(key => ({
        id: key,
        ...adminQueueData[key]
      }));
      
      console.log("🔍 Processed admin queue bookings:", adminQueueBookings);

      dispatch({
        type: ADMIN_QUEUE_ACTIONS.FETCH_ADMIN_QUEUE_SUCCESS,
        payload: adminQueueBookings
      });
    } else {
      console.log("🔍 No admin queue bookings found");
      dispatch({
        type: ADMIN_QUEUE_ACTIONS.FETCH_ADMIN_QUEUE_SUCCESS,
        payload: []
      });
    }
  } catch (error) {
    console.error('Error fetching admin queue:', error);
    dispatch({
      type: ADMIN_QUEUE_ACTIONS.FETCH_ADMIN_QUEUE_FAILED,
      payload: error.message
    });
  }
};

// 🚀 SIMPLE NOTIFICATION: Send notification to drivers when order enters ADMIN_QUEUE
export const sendAdminQueueNotification = async (bookingId, bookingData) => {
  const { usersRef, queueSettingsRef } = firebase;
  
  try {
    console.log('🔔 Checking if admin queue notification should be sent for booking:', bookingId);
    console.log('🔔 Booking data:', bookingData);
    
    // Get admin configuration to check if automaticNotification is enabled
    const adminConfigSnapshot = await get(queueSettingsRef);
    if (!adminConfigSnapshot.exists()) {
      console.log('⚠️ Admin config not found, skipping admin queue notification');
      return;
    }
    
    const adminConfig = adminConfigSnapshot.val();
    console.log('🔔 Admin config loaded:', adminConfig);
    
    // Check if automaticNotification is enabled
    if (!adminConfig.automaticNotification) {
      console.log('🔕 Admin config: automaticNotification disabled, skipping admin queue notification');
      return;
    }
    
    console.log('🔔 Admin config: automaticNotification enabled, sending admin queue notification');
    
    // Get notification text from admin config or use multi-language fallback
    let notificationTitle = adminConfig.notificationTitle || 
      store.getState().languagedata.defaultLanguage.admin_queue_notification_title || 
      'New Trip Available';
    let notificationMessage = adminConfig.notificationMessage || 
      store.getState().languagedata.defaultLanguage.admin_queue_notification_message || 
      'A new trip is available in admin queue';
    
    console.log('🔔 Notification content:', { notificationTitle, notificationMessage });
    
    // Find available drivers (online, in queue, matching car type)
    const availableDriversQuery = query(
      usersRef,
      orderByChild('usertype'),
      equalTo('driver')
    );
    
    const driversSnapshot = await get(availableDriversQuery);
    console.log('🔍 Drivers snapshot exists:', driversSnapshot.exists());
    
    let notifiedDrivers = 0;
    let totalDrivers = 0;
    let availableDrivers = 0;
    
    if (driversSnapshot.exists()) {
      const drivers = driversSnapshot.val();
      totalDrivers = Object.keys(drivers).length;
      console.log(`🔍 Found ${totalDrivers} total drivers in database`);
      
              Object.keys(drivers).forEach(driverId => {
          const driver = drivers[driverId];
          console.log(`🔍 Checking driver ${driverId}:`, {
            name: driver.firstName || driver.name,
            driverActiveStatus: driver.driverActiveStatus,
            queue: driver.queue,
            carType: driver.carType,
            hasPushToken: !!driver.pushToken,
            pushToken: driver.pushToken ? `${driver.pushToken.substring(0, 20)}...` : 'null'
          });
        
        // Check if driver is available for this trip
        if (isDriverAvailableForTrip(driver, bookingData)) {
          availableDrivers++;
          console.log(`✅ Driver ${driver.firstName || driver.name} is available, sending notification...`);
          // Send notification to this driver
          sendNotificationToDriver(driver, notificationTitle, notificationMessage, bookingId);
          notifiedDrivers++;
        } else {
          console.log(`❌ Driver ${driver.firstName || driver.name} is NOT available`);
        }
      });
    }
    
    console.log(`🔔 Admin queue notification summary:`);
    console.log(`   - Total drivers in database: ${totalDrivers}`);
    console.log(`   - Available drivers: ${availableDrivers}`);
    console.log(`   - Notifications sent: ${notifiedDrivers}`);
    
  } catch (error) {
    console.error('❌ Error sending admin queue notification:', error);
  }
};

// Helper function to check if driver is available for a specific trip
const isDriverAvailableForTrip = (driver, bookingData) => {
  console.log(`🔍 Checking driver availability: ${driver.firstName || driver.name}`, {
    driverActiveStatus: driver.driverActiveStatus,
    queue: driver.queue,
    carType: driver.carType,
    hasPushToken: !!driver.pushToken
  });
  
  // Driver must be active (driverActiveStatus = true)
  if (!driver.driverActiveStatus) {
    console.log(`❌ Driver ${driver.firstName || driver.name}: Not active (driverActiveStatus = false)`);
    return false;
  }
  
  // Driver must NOT be in queue (queue false = available, queue true = busy)
  if (driver.queue) {
    console.log(`❌ Driver ${driver.firstName || driver.name}: Busy (queue = true)`);
    return false;
  }
  
  // Check car type compatibility if specified
  if (bookingData.carType && driver.carType && bookingData.carType !== driver.carType) {
    console.log(`❌ Driver ${driver.firstName || driver.name}: Car type mismatch (${driver.carType} vs ${bookingData.carType})`);
    return false;
  }
  
  // Check if driver has push token
  if (!driver.pushToken) {
    console.log(`❌ Driver ${driver.firstName || driver.name}: No push token`);
    return false;
  }
  
  console.log(`✅ Driver ${driver.firstName || driver.name}: Available for trip`);
  return true;
};

// Helper function to send notification to a specific driver
const sendNotificationToDriver = async (driver, title, message, bookingId) => {
  try {
    console.log(`🔔 Sending notification to driver ${driver.firstName || driver.name}:`);
    console.log(`   - Push token: ${driver.pushToken ? `${driver.pushToken.substring(0, 20)}...` : 'null'}`);
    console.log(`   - Title: ${title}`);
    console.log(`   - Message: ${message}`);
    console.log(`   - Screen: RideList`);
    console.log(`   - Channel: admin_queue`);
    
    if (!driver.pushToken) {
      console.log(`❌ Driver ${driver.firstName || driver.name} has no push token, skipping notification`);
      return;
    }
    
    await RequestPushMsg(
      driver.pushToken,
      {
        title: title,
        msg: message,
        screen: 'RideList',
        params: { fromAdminQueue: true, bookingId, activeTab: 'pending' },
        channelId: 'admin_queue',
        sound: 'general.mp3'
      }
    );
    console.log(`✅ Notification sent successfully to driver ${driver.firstName} ${driver.lastName}`);
  } catch (error) {
    console.error(`❌ Error sending notification to driver ${driver.firstName}:`, error);
  }
};

// Assign driver to admin queue booking (with complete vehicle data like task action)
export const assignDriverToBooking = (bookingId, driverId, driverData) => async (dispatch) => {
  // Check if firebase is initialized
  if (!firebase) {
    throw new Error('Firebase not initialized');
  }
  
  // Check if required references exist
  if (!firebase.carAddRef) {
    throw new Error('Firebase carAddRef not available');
  }
  
  const { singleBookingRef, singleUserRef, carAddRef } = firebase;
  
  dispatch({
    type: ADMIN_QUEUE_ACTIONS.ASSIGN_DRIVER,
    payload: { bookingId, driverId }
  });

  try {
    console.log('🚗 Starting driver assignment with complete vehicle data...');
    
    // Get the booking data
    const bookingSnapshot = await get(singleBookingRef(bookingId));
    if (!bookingSnapshot.exists()) {
      throw new Error('Booking not found');
    }

    const booking = bookingSnapshot.val();
    
    // Get driver profile data for complete information
    const driverSnapshot = await get(singleUserRef(driverId));
    if (!driverSnapshot.exists()) {
      throw new Error('Driver profile not found');
    }
    
    const driverProfile = driverSnapshot.val();
    console.log('👤 Driver profile data:', {
      name: driverProfile.firstName + ' ' + (driverProfile.lastName || ''),
      mobile: driverProfile.mobile,
      profileImage: driverProfile.profile_image,
      pushToken: driverProfile.pushToken,
      rating: driverProfile.rating
    });
    
    // Get fresh car data from cars collection (like task action)
    let freshCarData = {};
    try {
      const carsSnapshot = await get(carAddRef);
      if (carsSnapshot.exists()) {
        const cars = carsSnapshot.val();
        Object.keys(cars).forEach(carId => {
          const car = cars[carId];
          if (car.driver === driverId && car.active) {
            console.log('🚗 Found active car data:', car);
            freshCarData = {
              vehicleNumber: car.vehicleNumber || "",
              vehicleYear: car.vehicleYear || "",
              vehicleColor: car.vehicleColor || "",
              carType: car.carType || "",
              vehicleMake: car.vehicleMake || "",
              vehicleModel: car.vehicleModel || "",
              car_image: car.car_image || "",
              other_info: car.other_info || ""
            };
          }
        });
      }
    } catch (error) {
      console.log('⚠️ Error reading cars data, using profile data:', error);
    }
    
    // Use fresh car data if available, otherwise fallback to profile data
    const vehicleData = Object.keys(freshCarData).length > 0 ? freshCarData : {
      vehicleNumber: driverProfile.vehicleNumber || "",
      vehicleYear: driverProfile.vehicleYear || "",
      vehicleColor: driverProfile.vehicleColor || "",
      carType: driverProfile.carType || "",
      vehicleMake: driverProfile.vehicleMake || "",
      vehicleModel: driverProfile.vehicleModel || "",
      car_image: driverProfile.car_image || "",
      other_info: driverProfile.other_info || ""
    };
    
    console.log('🚗 Final vehicle data for booking:', vehicleData);
    
    // Update booking status to ACCEPTED and assign driver with complete data
    const updatedBooking = {
      ...booking,
      status: 'ACCEPTED',
      driver: driverId,
      // Clear auto search related fields when driver is assigned
      autoSearchActive: null,
      autoSearchTimestamp: null,
      automaticSearch: null,
      cancelledBy: null,
      cardPaymentAmount: null,
      cashPaymentAmount: null,
      discount: null,
      dropAdress: null,
      id: null,
      pickupAddress: null,
      previousDriver: null,
      previousDriverToken: null,
      reason: null,
      wasAccepted: null,
      // Driver information
      driver_name: driverProfile.firstName + ' ' + (driverProfile.lastName || ''),
      driver_phone: driverProfile.mobile || driverProfile.phone || "",
      driver_contact: driverProfile.mobile || driverProfile.phone || "",
      driver_image: driverProfile.profile_image || "",
      driver_token: driverProfile.pushToken || "",
      driverRating: driverProfile.rating || "0",
      // Vehicle information (complete like task action)
      vehicle_number: vehicleData.vehicleNumber,
      vehicleNumber: vehicleData.vehicleNumber,
      vehicleYear: vehicleData.vehicleYear,
      vehicleColor: vehicleData.vehicleColor,
      carType: vehicleData.carType,
      vehicleMake: vehicleData.vehicleMake,
      vehicleModel: vehicleData.vehicleModel,
      car_image: vehicleData.car_image,
      other_info: vehicleData.other_info,
      // Assignment information
      assigned_at: new Date().toISOString(),
      assigned_by: 'admin',
      admin_queue_removed: true,
      // Additional driver info
      fleetadmin: driverProfile.fleetadmin || "",
      driverDeviceId: driverProfile.deviceId || null
    };

    console.log('📝 Updating booking with complete driver and vehicle data...');

    // Update booking in main bookings table
    await update(singleBookingRef(bookingId), updatedBooking);
    
    // Update driver status to busy
    await update(singleUserRef(driverId), { 
      queue: true, 
      current_booking: bookingId
    });

    console.log('✅ Driver status updated to busy');

    // Send notification to driver using admin config
    if (driverProfile.pushToken) {
      // Get admin configuration for notification
      let notificationTitle = store.getState().languagedata.defaultLanguage.trip_assigned_title || 'Trip Assigned';
      let notificationMessage = store.getState().languagedata.defaultLanguage.trip_assigned_message || `A trip has been assigned to you. Order ID: ${bookingId}`;
      
      try {
        const adminConfigSnapshot = await get(queueSettingsRef);
        if (adminConfigSnapshot.exists()) {
          const adminConfig = adminConfigSnapshot.val();
          if (adminConfig.notificationTitle) {
            notificationTitle = adminConfig.notificationTitle;
          }
          if (adminConfig.notificationMessage) {
            notificationMessage = adminConfig.notificationMessage;
          }
          console.log('🔔 Using admin config for notification:', { notificationTitle, notificationMessage });
        }
      } catch (configError) {
        console.log('⚠️ Could not load admin config, using default notification');
      }
      
      // Replace placeholder with actual order ID
      notificationMessage = notificationMessage.replace('{{orderId}}', bookingId);
      
      await RequestPushMsg(
        driverProfile.pushToken,
        {
          title: notificationTitle,
          msg: notificationMessage,
          screen: 'DriverTrips',
          params: { bookingId },
          channelId: 'general',
          sound: 'general.mp3'
        }
      );
      console.log('🔔 Notification sent to driver with admin config');
    }

    // Send notification to customer using admin config
    if (booking.customer_token) {
      // Get admin configuration for notification
      let customerNotificationTitle = 'Driver Assigned';
      let customerNotificationMessage = `A driver has been assigned to your trip. Order ID: ${bookingId}`;
      
      try {
        const adminConfigSnapshot = await get(queueSettingsRef);
        if (adminConfigSnapshot.exists()) {
          const adminConfig = adminConfigSnapshot.val();
          if (adminConfig.notificationTitle) {
            customerNotificationTitle = adminConfig.notificationTitle;
          }
          if (adminConfig.notificationMessage) {
            customerNotificationMessage = adminConfig.notificationMessage;
          }
          console.log('🔔 Using admin config for customer notification:', { customerNotificationTitle, customerNotificationMessage });
        }
      } catch (configError) {
        console.log('⚠️ Could not load admin config for customer, using default notification');
      }
      
      await RequestPushMsg(
        booking.customer_token,
        {
          title: customerNotificationTitle,
          msg: customerNotificationMessage,
          screen: 'BookedCab',
          params: { bookingId },
          channelId: 'general',
          sound: 'general.mp3'
        }
      );
      console.log('🔔 Notification sent to customer with admin config');
    }

    dispatch({
      type: ADMIN_QUEUE_ACTIONS.ASSIGN_DRIVER_SUCCESS,
      payload: { bookingId, driverId, updatedBooking }
    });

    console.log('✅ Driver assignment completed successfully');
    return { success: true, booking: updatedBooking };
    
  } catch (error) {
    console.error('❌ Error assigning driver:', error);
    dispatch({
      type: ADMIN_QUEUE_ACTIONS.ASSIGN_DRIVER_FAILED,
      payload: error.message
    });
    return { success: false, error: error.message };
  }
};

// Cancel trip from admin queue
export const cancelTripFromAdminQueue = (bookingId, reason = 'Cancelled by admin') => async (dispatch) => {
  const { singleBookingRef } = firebase;
  
  dispatch({
    type: ADMIN_QUEUE_ACTIONS.CANCEL_TRIP,
    payload: { bookingId, reason }
  });

  try {
    // Get the booking data
    const bookingSnapshot = await get(singleBookingRef(bookingId));
    if (!bookingSnapshot.exists()) {
      throw new Error('Booking not found');
    }

    const booking = bookingSnapshot.val();
    
    // Update booking status to CANCELLED
    const updatedBooking = {
      ...booking,
      status: 'CANCELLED',
      cancelled_at: new Date().toISOString(),
      cancelled_by: 'admin',
      cancellation_reason: reason,
      admin_queue_removed: true
    };

    // Update booking in main bookings table
    await update(singleBookingRef(bookingId), updatedBooking);

    // Send notification to customer
    if (booking.customer_token) {
      await RequestPushMsg(
        booking.customer_token,
        {
          title: 'Trip Cancelled',
          msg: `Your trip has been cancelled. Reason: ${reason}. Order ID: ${bookingId}`,
          screen: 'BookedCab',
          params: { bookingId },
          channelId: 'general',
          sound: 'general.mp3'
        }
      );
    }

    dispatch({
      type: ADMIN_QUEUE_ACTIONS.CANCEL_TRIP_SUCCESS,
      payload: { bookingId, reason }
    });

    return { success: true };
    
  } catch (error) {
    console.error('Error cancelling trip:', error);
    dispatch({
      type: ADMIN_QUEUE_ACTIONS.CANCEL_TRIP_FAILED,
      payload: error.message
    });
    return { success: false, error: error.message };
  }
};

// Get available drivers for assignment (following mobile app logic)
export const getAvailableDrivers = async (pickupLocation, radius = 10) => {
  const { usersRef, allLocationsRef, settingsRef } = firebase;
  
  try {
    
    // Get settings for driver requirements
    const settingsSnapshot = await get(settingsRef);
    const settings = settingsSnapshot.val() || {};
    
    // Get all users and locations
    const [usersSnapshot, locationsSnapshot] = await Promise.all([
      get(usersRef),
      get(allLocationsRef)
    ]);
    
    if (!usersSnapshot.exists()) {
      console.log('❌ No users found in database');
      return [];
    }
    
    const users = usersSnapshot.val();
    const locations = locationsSnapshot.val() || {};
    
    // Filter drivers using same logic as mobile app
    const eligibleDrivers = Object.keys(users).filter(userId => {
      const driver = users[userId];
      const location = locations[userId];
      
      // Check all required conditions (same as mobile app)
      const isDriver = driver.usertype === 'driver';
      const isApproved = driver.approved === true;
      const isActive = driver.driverActiveStatus === true;
      const isAvailable = driver.queue === false; // queue false = available, queue true = busy
      const hasLocation = location && location.lat && location.lng;
      const hasLicense = (driver.licenseImage && settings.license_image_required) || !settings.license_image_required;
      const hasCar = (driver.carApproved && settings.carType_required) || !settings.carType_required;
      const hasTerm = (driver.term && settings.term_required) || !settings.term_required;
      
      const isEligible = isDriver && isApproved && isActive && isAvailable && hasLocation && hasLicense && hasCar && hasTerm;
      
      if (isDriver) {
        console.log(`🚗 Driver ${driver.firstName || driver.name || userId}:`, {
          approved: isApproved,
          active: isActive,
          available: isAvailable, // queue = false
          busy: !isAvailable,     // queue = true
          hasLocation: hasLocation,
          hasLicense: hasLicense,
          hasCar: hasCar,
          hasTerm: hasTerm,
          eligible: isEligible
        });
      }
      
      return isEligible;
    });
    
    // Calculate distances and filter by radius
    const availableDrivers = [];
    let inRadiusCount = 0;

    eligibleDrivers.forEach(userId => {
      const driver = users[userId];
      const location = locations[userId];
      
      if (location && pickupLocation) {
          const distance = calculateDistance(
          location.lat,
          location.lng,
            pickupLocation.lat,
            pickupLocation.lng
          );
        
          if (distance <= radius) {
          inRadiusCount++;
            availableDrivers.push({
              id: userId,
            name: driver.firstName + ' ' + (driver.lastName || ''),
            phone: driver.mobile || driver.phone,
            vehicle: driver.vehicleNumber || driver.vehicle,
            carType: driver.carType,
            rating: driver.rating || 0,
              distance: distance,
            token: driver.pushToken,
            current_location: location,
            category: driver.category,
            queue: driver.queue
          });
        }
      }
    });
    // Sort by distance (nearest first)
    const sortedDrivers = availableDrivers.sort((a, b) => a.distance - b.distance);
    
    return sortedDrivers;
    
  } catch (error) {
    console.error('❌ Error getting available drivers:', error);
    return [];
  }
};

// Calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Update admin configuration
export const updateAdminConfiguration = (config) => async (dispatch) => {
  const { queueSettingsRef } = firebase;
  
  dispatch({
    type: ADMIN_QUEUE_ACTIONS.UPDATE_ADMIN_CONFIG,
    payload: config
  });

  try {
    await update(queueSettingsRef, {
      ...config,
      updated_at: new Date().toISOString(),
      updated_by: store.getState().auth.profile?.uid || 'admin'
    });

    dispatch({
      type: ADMIN_QUEUE_ACTIONS.UPDATE_ADMIN_CONFIG_SUCCESS,
      payload: config
    });

    return { success: true };
    
  } catch (error) {
    console.error('Error updating admin config:', error);
    dispatch({
      type: ADMIN_QUEUE_ACTIONS.UPDATE_ADMIN_CONFIG_FAILED,
      payload: error.message
    });
    return { success: false, error: error.message };
  }
};

// Get admin configuration
export const getAdminConfiguration = async () => {
  const { queueSettingsRef, settingsRef } = firebase;
  
  try {
    // Get both queue settings and general settings
    const [queueSnapshot, settingsSnapshot] = await Promise.all([
      get(queueSettingsRef),
      get(settingsRef)
    ]);
    
    // Get queue settings
    const queueSettings = queueSnapshot.exists() ? queueSnapshot.val() : {
      automaticNotification: false,
      automaticAssignment: false,
      automaticSearch: false,
      autoAssignmentTimeout: 30
    };
    
    // Get general settings to check if admin queue addon is enabled
    const generalSettings = settingsSnapshot.exists() ? settingsSnapshot.val() : {};
    
    // Get driverRadius from general settings
    const driverRadius = generalSettings.driverRadius || 10;
    const isAdminQueueAddonEnabled = generalSettings.admin_queue_addon_enabled === true;
    
    console.log('🔍 Admin Queue Add-On enabled:', isAdminQueueAddonEnabled);
    console.log('🔍 Queue Settings:', queueSettings);
    
    // If admin queue addon is disabled, force all automatic features to false
    if (!isAdminQueueAddonEnabled) {
      console.log('⚠️ Admin Queue Add-On is disabled - forcing all automatic features to false');
      return {
        ...queueSettings,
        automaticNotification: false,
        automaticAssignment: false,
        automaticSearch: false,
        driverRadius: driverRadius // Use driverRadius from general settings
      };
    }
    
    // Return queue settings with driverRadius from general settings
    return {
      ...queueSettings,
      driverRadius: driverRadius,
      notificationRadius: driverRadius
    };
    
  } catch (error) {
    console.error('Error getting admin config:', error);
    return null;
  }
};
